import {
  getWarningLevel,
  sortWarningsBySeverity,
} from '@src/shared/lib/warnings';

import type {
  UserProfileSection,
  UserWarningDefinitionDto,
  UserWarningDefinitionsDto,
  UserWarningDto,
  UserWarningEvaluatePayload,
  UserWarningListSummaryDto,
  UserWarningSectionsDto,
} from '../types';

const USER_WARNING_SECTION_BY_FIELD: Record<string, UserProfileSection> = {
  email: 'contacts',
  language: 'personal',
  salary_saly_end: 'professional',
  salary_saly_hourly: 'professional',
  salary_saly_monthly: 'professional',
  salary_saly_monthly_salary: 'professional',
  salary_saly_percentage: 'professional',
  salary_saly_rank: 'professional',
  salary_saly_start: 'professional',
  user_address_line_1: 'location',
  user_address_line_2: 'location',
  user_bran_workplace: 'professional',
  user_birthdate: 'personal',
  user_city: 'location',
  user_ctry: 'location',
  user_dept: 'professional',
  user_email_private: 'contacts',
  user_emergency_contact1: 'emergency',
  user_emergency_contact2: 'emergency',
  user_emergency_email1: 'emergency',
  user_emergency_email2: 'emergency',
  user_emergency_mobile1: 'emergency',
  user_emergency_mobile2: 'emergency',
  user_emergency_relationship1: 'emergency',
  user_emergency_relationship2: 'emergency',
  user_family_name: 'personal',
  user_first_name: 'personal',
  user_initials: 'personal',
  user_joti: 'professional',
  user_mobile: 'contacts',
  user_mobile_private: 'contacts',
  user_phone: 'contacts',
  user_phone_private: 'contacts',
  user_portrait: 'personal',
  user_postcode: 'location',
  user_signature: 'personal',
  user_team: 'professional',
  user_titl: 'personal',
  user_user_deputy: 'professional',
  user_user_hr: 'professional',
  user_user_manager: 'professional',
};

// Fields the warning payload builders actually send. Live evaluation can only
// react to these, so they are also the only trigger fields the client supports.
export const USER_WARNING_PAYLOAD_FIELDS = [
  'email',
  'user_initials',
  'user_birthdate',
  'user_email_private',
  'user_mobile_private',
  'user_user_manager',
  'user_user_hr',
  'user_user_deputy',
  'user_emergency_contact1',
  'user_emergency_mobile1',
  'user_emergency_email1',
  'user_address_line_1',
  'user_city',
  'user_postcode',
] as const satisfies readonly (keyof UserWarningEvaluatePayload)[];

const USER_WARNING_PRESENCE_FIELDS = [
  'portrait_present',
  'signature_present',
] as const satisfies readonly (keyof UserWarningEvaluatePayload)[];

const USER_WARNING_TRIGGER_FIELDS: ReadonlySet<string> = new Set([
  ...USER_WARNING_PAYLOAD_FIELDS,
  ...USER_WARNING_PRESENCE_FIELDS,
]);

const reportedDefinitionIssues = new Set<string>();

type WarningDefinitionValidationOptions = {
  report?: (message: string) => void;
  strict?: boolean;
};

export function getUserWarningDefinitionIssues(
  payload: UserWarningDefinitionsDto,
): string[] {
  const issues = new Set<string>();

  payload.definitions.forEach((definition) => {
    definition.fields.forEach((field) => {
      if (!Object.hasOwn(USER_WARNING_SECTION_BY_FIELD, field)) {
        issues.add(
          `Warning definition "${definition.code}" has an unmapped display field "${field}".`,
        );
      }
    });
    definition.trigger_fields.forEach((field) => {
      if (!USER_WARNING_TRIGGER_FIELDS.has(field)) {
        issues.add(
          `Warning definition "${definition.code}" has an unknown trigger field "${field}".`,
        );
      }
    });
  });

  Object.keys(payload.triggers).forEach((field) => {
    if (!USER_WARNING_TRIGGER_FIELDS.has(field)) {
      issues.add(
        `Warning triggers contain an unknown payload field "${field}".`,
      );
    }
  });

  return [...issues].sort();
}

export function validateUserWarningDefinitions(
  payload: UserWarningDefinitionsDto,
  options: WarningDefinitionValidationOptions = {},
): UserWarningDefinitionsDto {
  const issues = getUserWarningDefinitionIssues(payload);
  if (issues.length === 0) {
    return payload;
  }

  const message = `Invalid user warning definitions:\n${issues.join('\n')}`;
  // Report before throwing: the query layer keeps an error thrown from
  // `select` in its error state without logging it.
  if (!reportedDefinitionIssues.has(message)) {
    reportedDefinitionIssues.add(message);
    (options.report ?? console.error)(message);
  }

  const strict = options.strict ?? !import.meta.env.PROD;
  if (strict) {
    throw new Error(message);
  }
  return payload;
}

export function getUserWarningSections(
  fields: readonly string[],
): Set<UserProfileSection> {
  return new Set(
    fields.flatMap((field) => {
      const section = USER_WARNING_SECTION_BY_FIELD[field];
      return section ? [section] : [];
    }),
  );
}

export function isUserWarningInSection(
  warning: UserWarningDto,
  section: UserProfileSection,
) {
  return getUserWarningSections(warning.fields).has(section);
}

// Codes of definitions whose trigger fields all still hold their last
// evaluated value, so their previously shown result is current.
export function getUnchangedUserWarningCodes(
  definitions: readonly UserWarningDefinitionDto[],
  changedFields: ReadonlySet<string>,
): string[] {
  return definitions
    .filter((definition) =>
      definition.trigger_fields.every((field) => !changedFields.has(field)),
    )
    .map((definition) => definition.code);
}

export function getUserWarningDefinitionsInSections(
  definitions: readonly UserWarningDefinitionDto[],
  sections: ReadonlySet<string>,
): UserWarningDefinitionDto[] {
  return definitions.filter((definition) =>
    [...getUserWarningSections(definition.fields)].some((section) =>
      sections.has(section),
    ),
  );
}

export function getUserWarningTriggerFields(
  definitions: readonly UserWarningDefinitionDto[],
): string[] {
  return [
    ...new Set(definitions.flatMap((definition) => definition.trigger_fields)),
  ];
}

// Rebuilds the last-evaluated payload after persisted data or the set of
// draft sections changes. Trigger fields of sections that are still being
// edited keep their previous evaluated values: taking the current draft
// values instead would mark an evaluation that never completed as done.
export function rebaseUserWarningEvaluatedPayload(
  persistedPayload: UserWarningEvaluatePayload,
  previousEvaluatedPayload: UserWarningEvaluatePayload,
  draftSections: ReadonlySet<string>,
  definitions: readonly UserWarningDefinitionDto[],
): UserWarningEvaluatePayload {
  const next = { ...persistedPayload };
  const draftTriggerFields = getUserWarningTriggerFields(
    getUserWarningDefinitionsInSections(definitions, draftSections),
  );

  draftTriggerFields.forEach((field) => {
    const key = field as keyof UserWarningEvaluatePayload;
    if (Object.hasOwn(previousEvaluatedPayload, key)) {
      Object.assign(next, { [key]: previousEvaluatedPayload[key] });
    } else {
      delete next[key];
    }
  });
  return next;
}

// Decides which parts of an evaluate response may still be applied when it
// arrives. A code is accepted only if none of its trigger fields changed
// while the request was in flight; a field counts as evaluated only if every
// code it triggers was accepted, so a rejected code is requested again.
//
// A code missing from the cached definitions has unknown trigger fields. The
// server only returns codes triggered by the request's changed fields, so
// such a code is accepted only if all of those are still current; if it is
// rejected, no field of the request counts as evaluated.
export function resolveUserWarningEvaluation({
  changedFields,
  definitions,
  evaluatedCodes,
  isFieldCurrent,
}: {
  changedFields: readonly string[];
  definitions: readonly UserWarningDefinitionDto[];
  evaluatedCodes: readonly string[];
  isFieldCurrent: (field: string) => boolean;
}): { acceptedCodes: Set<string>; acceptedFields: string[] } {
  const definitionByCode = new Map(
    definitions.map((definition) => [definition.code, definition]),
  );
  const allChangedFieldsCurrent = changedFields.every(isFieldCurrent);
  const acceptedCodes = new Set(
    evaluatedCodes.filter((code) => {
      const definition = definitionByCode.get(code);
      return definition
        ? definition.trigger_fields.every(isFieldCurrent)
        : allChangedFieldsCurrent;
    }),
  );
  const hasRejectedUnknownCode = evaluatedCodes.some(
    (code) => !definitionByCode.has(code) && !acceptedCodes.has(code),
  );
  const acceptedFields = hasRejectedUnknownCode
    ? []
    : changedFields.filter(
        (field) =>
          isFieldCurrent(field) &&
          definitions
            .filter((definition) => definition.trigger_fields.includes(field))
            .every((definition) => acceptedCodes.has(definition.code)),
      );

  return { acceptedCodes, acceptedFields };
}

export function isUserWarningDefinitionInSection(
  definition: UserWarningDefinitionDto,
  section: UserProfileSection,
) {
  return getUserWarningSections(definition.fields).has(section);
}

export function getUserWarningLevel(
  warnings: readonly UserWarningDto[],
): UserWarningListSummaryDto['level'] {
  return getWarningLevel(warnings);
}

export function buildUserWarningSummary(
  warnings: readonly UserWarningDto[],
): UserWarningListSummaryDto {
  const sortedWarnings = sortWarningsBySeverity(warnings);
  return {
    level: getUserWarningLevel(sortedWarnings),
    count: sortedWarnings.length,
    warnings: sortedWarnings,
  };
}

export function buildUserWarningSections(
  warnings: readonly UserWarningDto[],
  visibleSections?: ReadonlySet<UserProfileSection>,
): UserWarningSectionsDto {
  const sections: UserWarningSectionsDto = {};

  sortWarningsBySeverity(warnings).forEach((warning) => {
    getUserWarningSections(warning.fields).forEach((section) => {
      if (visibleSections && !visibleSections.has(section)) {
        return;
      }
      const current = sections[section];
      const sectionWarnings = current
        ? [...current.warnings, warning]
        : [warning];
      sections[section] = {
        level: getUserWarningLevel(sectionWarnings),
        count: sectionWarnings.length,
        warnings: sectionWarnings,
      };
    });
  });

  return sections;
}
